import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  DatePicker,
  InputNumber,
  Switch,
  Button,
  Collapse,
  Checkbox,
  Empty,
  Typography,
  Tag,
} from "antd";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerSummaries } from "../../redux/customer.slice";
import { fetchForSaleProducts } from "../../redux/product.slice";
import dayjs from "dayjs";
import { canSee } from "../../../core/utils/permissions";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PrinterOutlined, RightOutlined } from "@ant-design/icons";
import LoadingState from "../../common/LoadingState";
import { usePrintContext } from "../../utils/printContext";
import { useTranslation } from "react-i18next";
const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const ContractFormModal = ({
  open,
  onCancel,
  onSubmit,
  isEdit,
  initialData,
  readOnly = false,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customers, loading: customersLoading } = useSelector(
    (state) => state.customer,
  );
  const { products, loading: productsLoading } = useSelector(
    (state) => state.product,
  );
  const { user } = useSelector((state) => state.auth);

  const isAdmin =
    canSee(user?.roles, "ADMIN") || canSee(user?.roles, "SUPERADMIN");
  const effectiveReadOnly = readOnly || !isAdmin;
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    dispatch(fetchCustomerSummaries(false));
    dispatch(fetchForSaleProducts());
  }, [dispatch]);
  const { getPdfColor } = usePrintContext();

  const normalizedProducts = useMemo(() => {
    return (products || []).map((product) => ({
      id: product.id || product.productId,
      name:
        product.productName ||
        product.name ||
        product.productLabel ||
        `Produit ${product.id || product.productId}`,
      reference: product.reference || product.productReference || "",
      modelId: product.modelId || product.model?.id || null,
      modelName: product.modelName || product.model?.name || null,
    }));
  }, [products]);

  const normalizedModels = useMemo(() => {
    const map = new Map();

    (products || []).forEach((product) => {
      const modelId = product.modelId || product.model?.id;
      const modelName = product.modelName || product.model?.name;

      if (modelId && !map.has(modelId)) {
        map.set(modelId, {
          id: modelId,
          name: modelName || `${t("contracts.fields.subFamily")}${modelId}`,
        });
      }
    });

    return Array.from(map.values());
  }, [products, t]);

  const productsByModel = useMemo(() => {
    return normalizedModels.map((model) => ({
      ...model,
      products: normalizedProducts.filter(
        (product) => product.modelId === model.id,
      ),
    }));
  }, [normalizedModels, normalizedProducts]);

  const isDataReady =
    !customersLoading &&
    !productsLoading &&
    customers.length > 0 &&
    productsByModel.length > 0;

  const defaultSelections = useMemo(() => {
    return productsByModel.map((model) => ({
      modelId: model.id,
      checkedAll: false,
      checkedProductIds: [],
      percent: 0,
    }));
  }, [productsByModel]);

  const initialSelections = useMemo(() => {
    const grouped = new Map();

    (initialData?.contractLinesDto || []).forEach((line) => {
      const product = normalizedProducts.find((p) => p.id === line.productId);
      if (!product?.modelId) return;

      const key = product.modelId;

      if (!grouped.has(key)) {
        grouped.set(key, {
          modelId: key,
          checkedAll: false,
          checkedProductIds: [],
          percent: Number(line.totalLineDiscountPercent ?? 0),
        });
      }

      const current = grouped.get(key);
      current.checkedProductIds.push(product.id);

      if (current.percent === undefined || current.percent === null) {
        current.percent = Number(line.totalLineDiscountPercent ?? 0);
      }
    });

    return productsByModel.map((model) => {
      const existing = grouped.get(model.id);

      if (!existing) {
        return {
          modelId: model.id,
          checkedAll: false,
          checkedProductIds: [],
          percent: 0,
        };
      }

      const allProductIds = model.products.map((p) => p.id);
      const checkedProductIds = Array.from(new Set(existing.checkedProductIds));
      const checkedAll =
        allProductIds.length > 0 &&
        checkedProductIds.length === allProductIds.length;

      return {
        modelId: model.id,
        checkedAll,
        checkedProductIds,
        percent: Number(existing.percent ?? 0),
      };
    });
  }, [initialData, normalizedProducts, productsByModel]);

  useEffect(() => {
    if (!open || !isDataReady) return;

    if (isEdit && initialData) {
      form.setFieldsValue({
        ...initialData,
        contractStart: initialData.contractStart
          ? dayjs(initialData.contractStart)
          : null,
        contractEnd: initialData.contractEnd
          ? dayjs(initialData.contractEnd)
          : null,
        contractSelections: initialSelections,
      });
    } else {
      form.setFieldsValue({
        contractIsActive: true,
        contractSelections: defaultSelections,
      });
    }
  }, [
    open,
    isEdit,
    initialData,

    isDataReady,
    form,
    defaultSelections,
    initialSelections,
  ]);

  const watchedSelectionsRaw = Form.useWatch("contractSelections", form);
  const watchedSelections = useMemo(
    () => watchedSelectionsRaw || [],
    [watchedSelectionsRaw],
  );

  const selectedProductsCount = useMemo(() => {
    return watchedSelections.reduce((total, selection) => {
      if (selection?.checkedAll) {
        const model = productsByModel.find((m) => m.id === selection.modelId);
        return total + (model?.products?.length || 0);
      }

      return total + (selection?.checkedProductIds?.length || 0);
    }, 0);
  }, [watchedSelections, productsByModel]);

  const updateModelSelection = (index, patch) => {
    const currentSelections = form.getFieldValue("contractSelections") || [];
    const updatedSelections = [...currentSelections];
    updatedSelections[index] = {
      ...updatedSelections[index],
      ...patch,
    };
    form.setFieldsValue({ contractSelections: updatedSelections });
  };

  const handleToggleAllProducts = (index, checked, allProductIds) => {
    updateModelSelection(index, {
      checkedAll: checked,
      checkedProductIds: checked ? allProductIds : [],
    });
  };

  const handleProductsChange = (index, selectedIds, allProductIds) => {
    const uniqueSelectedIds = Array.from(new Set(selectedIds || []));
    const checkedAll =
      allProductIds.length > 0 &&
      uniqueSelectedIds.length === allProductIds.length;

    updateModelSelection(index, {
      checkedAll,
      checkedProductIds: uniqueSelectedIds,
    });
  };

  const handleOk = async () => {
    if (effectiveReadOnly || submitting) return;

    try {
      const values = await form.validateFields();
      const selections = values.contractSelections || [];

      const expandedContractLines = selections.flatMap((selection) => {
        const percent = Number(selection?.percent || 0);
        const model = productsByModel.find((m) => m.id === selection?.modelId);

        if (!model) return [];

        const productIds = selection?.checkedAll
          ? model.products.map((product) => product.id)
          : selection?.checkedProductIds || [];

        return productIds.map((productId) => ({
          productId,
          totalLineDiscountPercent: percent,
        }));
      });

      const uniqueContractLinesMap = new Map();

      expandedContractLines.forEach((line) => {
        if (!line.productId) return;

        uniqueContractLinesMap.set(line.productId, {
          productId: line.productId,
          totalLineDiscountPercent: line.totalLineDiscountPercent,
        });
      });

      const contractLinesDto = Array.from(uniqueContractLinesMap.values()).map(
        (line, index) => ({
          ...line,
          contractLineNumber: `CL-${index + 1}`,
        }),
      );

      const formattedValues = {
        ...values,
        contractStart: values.contractStart?.format("YYYY-MM-DD"),
        contractEnd: values.contractEnd?.format("YYYY-MM-DD"),
        contractLinesDto,
      };

      delete formattedValues.contractSelections;

      setSubmitting(true);

      const payload = isEdit
        ? { ...initialData, ...formattedValues }
        : formattedValues;

      await Promise.resolve(
        onSubmit(payload, () => {
          form.resetFields();
          setSubmitting(false);
        }),
      );
    } catch (error) {
      setSubmitting(false);
      console.log("Validate Failed:", error);
    }
  };

  const handlePrintContract = async () => {
    try {
      const values = form.getFieldsValue(true);
      const customer = customers.find(
        (c) => c.customerId === values.customerId,
      );
      const selections = values.contractSelections || [];

      const expandedLines = selections.flatMap((selection) => {
        const model = productsByModel.find((m) => m.id === selection?.modelId);
        if (!model) return [];

        const selectedProducts = selection?.checkedAll
          ? model.products
          : model.products.filter((product) =>
              (selection?.checkedProductIds || []).includes(product.id),
            );

        return selectedProducts.map((product) => ({
          modelName: model.name || t("contracts.fields.noSubFamily"),
          productName: product.reference
            ? `${product.name} (${product.reference})`
            : product.name,
          percent: Number(selection?.percent || 0),
        }));
      });

      const groupedMap = expandedLines.reduce((acc, line) => {
        const key = line.modelName || t("contracts.fields.noSubFamily");

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(line);
        return acc;
      }, {});

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const marginLeft = 15;
      const marginRight = 15;
      const marginTop = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - marginLeft - marginRight;

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20);
      doc.text(
        t("contracts.print.title"),
        pageWidth - marginRight,
        marginTop + 5,
        {
          align: "right",
        },
      );

      let y = marginTop + 18;

      doc.setLineWidth(0.3);
      doc.line(marginLeft, y, pageWidth - marginRight, y);
      y += 8;

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(10);
      doc.text(t("contracts.print.general"), marginLeft, y);
      y += 6;

      doc.setFont("Helvetica", "normal");
      doc.setFontSize(10);

      const infoLines = [
        `${t("contracts.print.number")} : ${values.contractNumber || initialData?.contractNumber || "-"}`,
        `${t("contracts.print.customer")}: ${customer?.customerName || initialData?.customerName || "-"}`,
        `${t("contracts.print.startDate")}: ${
          values.contractStart
            ? dayjs(values.contractStart).format("DD/MM/YYYY")
            : initialData?.contractStart
              ? dayjs(initialData.contractStart).format("DD/MM/YYYY")
              : "-"
        }`,
        `${t("contracts.print.endDate")}: ${
          values.contractEnd
            ? dayjs(values.contractEnd).format("DD/MM/YYYY")
            : initialData?.contractEnd
              ? dayjs(initialData.contractEnd).format("DD/MM/YYYY")
              : "-"
        }`,
        `${t("contracts.print.status")}: ${values.contractIsActive ? t("contracts.status.active") : t("contracts.status.inactive")}`,
      ];

      infoLines.forEach((line) => {
        doc.text(line, marginLeft, y);
        y += 6;
      });

      const description =
        values.contractDescription || initialData?.contractDescription || "";

      if (description) {
        y += 2;
        doc.setFont("Helvetica", "bold");
        doc.text(t("contracts.print.description"), marginLeft, y);
        y += 5;

        doc.setFont("Helvetica", "normal");
        const splitDescription = doc.splitTextToSize(description, contentWidth);
        splitDescription.forEach((line) => {
          doc.text(line, marginLeft, y);
          y += 5;
        });
      }

      y += 4;

      if (Object.keys(groupedMap).length === 0) {
        doc.setFont("Helvetica", "italic");
        doc.setFontSize(10);
        doc.text(t("contracts.print.noProducts"), marginLeft, y);
        y += 8;
      }

      Object.entries(groupedMap).forEach(([modelName, lines]) => {
        if (y > pageHeight - 40) {
          doc.addPage();
          y = marginTop;
        }

        doc.setFont("Helvetica", "bold");
        doc.setFontSize(11);
        doc.text(
          `${t("contracts.print.subFamily")}: ${modelName}`,
          marginLeft,
          y,
        );
        y += 4;

        autoTable(doc, {
          startY: y,
          head: [[t("contracts.print.product"), t("contracts.print.discount")]],
          body: lines.map((line) => [
            line.productName,
            `${Number(line.percent).toFixed(2)}%`,
          ]),
          margin: { left: marginLeft, right: marginRight },
          styles: {
            font: "Helvetica",
            fontSize: 9,
            cellPadding: 2,
            valign: "middle",
          },
          headStyles: {
            fillColor: getPdfColor("primary"),
            textColor: 255,
            fontStyle: "bold",
          },
          columnStyles: {
            0: { cellWidth: contentWidth * 0.75 },
            1: { cellWidth: contentWidth * 0.25, halign: "right" },
          },
          theme: "striped",
        });

        y = (doc.lastAutoTable?.finalY || y) + 8;
      });

      const footerY = pageHeight - 15;
      doc.setFont("Helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `${t("contracts.print.printedDate")} ${dayjs().format("DD/MM/YYYY HH:mm")}`,
        pageWidth - marginRight,
        footerY,
        { align: "right" },
      );

      doc.autoPrint();
      const blobUrl = doc.output("bloburl");
      window.open(blobUrl);
    } catch (error) {
      console.error(t("contracts.messages.printError"), error);
    }
  };

  const modalBodyStyle = {
    maxHeight: "78vh",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 8,
  };

  const selectionContainerStyle = {
    maxHeight: 420,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 8,
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    padding: 12,
    background: "#fafafa",
  };

  const selectionHeaderStyle = {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: "1px solid #f0f0f0",
  };

  return (
    <Modal
      title={
        isEdit
          ? `${t("contracts.modal.editTitle")}${initialData?.contractNumber ? ` #${initialData.contractNumber}` : ""}`
          : effectiveReadOnly
            ? `${t("contracts.modal.detailsTitle")}${initialData?.contractNumber ? ` #${initialData.contractNumber}` : ""}`
            : t("contracts.modal.addTitle")
      }
      open={open}
      onOk={effectiveReadOnly ? undefined : handleOk}
      onCancel={onCancel}
      destroyOnHidden
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={submitting}
      width={980}
      okButtonProps={effectiveReadOnly ? { style: { display: "none" } } : {}}
      cancelButtonProps={{ disabled: submitting }}
      styles={{
        body: modalBodyStyle,
      }}
      afterOpenChange={(visible) => {
        if (!visible) {
          form.resetFields();
        }
      }}
    >
      {!isDataReady ? (
        <LoadingState type="modal" />
      ) : (
        <Form form={form} layout="vertical" name="contract_form">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customerId"
                label={t("contracts.columns.customer")}
                rules={[
                  {
                    required: true,
                    message: t("contracts.columns.clientAlert"),
                  },
                ]}
              >
                <Select
                  placeholder={t("contracts.modal.clientSelect")}
                  showSearch
                  optionFilterProp="children"
                  disabled={effectiveReadOnly}
                >
                  {customers.map((customer) => (
                    <Option
                      key={customer.customerId}
                      value={customer.customerId}
                    >
                      {customer.customerName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="contractIsActive"
                label={t("contracts.modal.contractStatus")}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch
                  checkedChildren="Actif"
                  unCheckedChildren="Inactif"
                  disabled={effectiveReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contractStart"
                label={t("contracts.print.startDate")}
                rules={[
                  {
                    required: true,
                    message: t("contracts.messsages.startDate"),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabled={effectiveReadOnly}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="contractEnd"
                label={t("contracts.print.endDate")}
                rules={[
                  { required: true, message: t("contracts.messsages.endDate") },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("contractStart");

                      if (!startDate || !value) {
                        return Promise.resolve();
                      }

                      if (dayjs(value).isBefore(dayjs(startDate), "day")) {
                        return Promise.reject(
                          new Error(
                            "La date de fin doit être supérieure ou égale à la date de début",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabled={effectiveReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="contractDescription"
                label={t("contracts.print.description")}
              >
                <TextArea
                  rows={2}
                  placeholder={t("contracts.messages.description")}
                  disabled={effectiveReadOnly}
                />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              ...selectionHeaderStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h4 style={{ margin: 0 }}>
              {selectedProductsCount}{" "}
              {selectedProductsCount > 1
                ? t("contracts.print.selectedProducts")
                : t("contracts.print.selectedProduct")}
            </h4>

            <Button
              className="btn-customized-primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintContract}
              disabled={submitting}
            >
              {t("common.print")}
            </Button>
          </div>

          <div style={selectionContainerStyle}>
            {productsByModel.length === 0 ? (
              <Empty description={t("contracts.print.NosubFamily")} />
            ) : (
              <Form.List name="contractSelections">
                {(fields) => (
                  <Collapse
                    bordered={false}
                    expandIcon={({ isActive }) => (
                      <RightOutlined rotate={isActive ? 90 : 0} />
                    )}
                    style={{ background: "transparent" }}
                    items={fields
                      .map((field) => {
                        const selection =
                          form.getFieldValue([
                            "contractSelections",
                            field.name,
                          ]) || {};
                        const model = productsByModel.find(
                          (item) => item.id === selection?.modelId,
                        );

                        if (!model) return null;

                        const allProductIds = model.products.map((p) => p.id);
                        const selectedIds = selection?.checkedProductIds || [];
                        const checkedAll =
                          !!selection?.checkedAll && allProductIds.length > 0;
                        const indeterminate =
                          !checkedAll &&
                          selectedIds.length > 0 &&
                          selectedIds.length < allProductIds.length;

                        return {
                          key: String(model.id),
                          label: (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 16,
                                width: "100%",
                                paddingRight: 12,
                                flexWrap: "wrap",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Checkbox
                                  checked={checkedAll}
                                  indeterminate={indeterminate}
                                  onChange={(e) =>
                                    handleToggleAllProducts(
                                      field.name,
                                      e.target.checked,
                                      allProductIds,
                                    )
                                  }
                                  disabled={
                                    effectiveReadOnly ||
                                    allProductIds.length === 0
                                  }
                                >
                                  <span onClick={(e) => e.stopPropagation()}>
                                    {model.name}
                                  </span>
                                </Checkbox>

                                <Tag color="blue">
                                  {selectedIds.length}/{allProductIds.length}{" "}
                                  produit(s)
                                </Tag>
                              </div>

                              <Form.Item
                                name={[field.name, "percent"]}
                                label={null}
                                rules={[
                                  {
                                    validator: (_, value) => {
                                      const hasSelection =
                                        checkedAll || selectedIds.length > 0;

                                      if (!hasSelection) {
                                        return Promise.resolve();
                                      }

                                      if (
                                        value === undefined ||
                                        value === null ||
                                        value === ""
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            "Le taux est obligatoire si au moins un produit est sélectionné",
                                          ),
                                        );
                                      }

                                      if (
                                        Number(value) < 0 ||
                                        Number(value) > 100
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            "Le taux doit être entre 0 et 100",
                                          ),
                                        );
                                      }

                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                                style={{ margin: 0, width: 150 }}
                              >
                                <InputNumber
                                  min={0}
                                  max={100}
                                  precision={2}
                                  style={{ width: "100%" }}
                                  addonAfter="%"
                                  disabled={effectiveReadOnly}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Form.Item>
                            </div>
                          ),
                          children: (
                            <div style={{ paddingTop: 4 }}>
                              {model.products.length === 0 ? (
                                <Text type="secondary">
                                  {t("contracts.print.NosubFamily")}
                                </Text>
                              ) : (
                                <Form.Item
                                  name={[field.name, "checkedProductIds"]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox.Group
                                    style={{ width: "100%" }}
                                    disabled={effectiveReadOnly}
                                    onChange={(checkedValues) =>
                                      handleProductsChange(
                                        field.name,
                                        checkedValues,
                                        allProductIds,
                                      )
                                    }
                                  >
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                          "repeat(auto-fit, minmax(280px, 1fr))",
                                        gap: 10,
                                      }}
                                    >
                                      {model.products.map((product) => (
                                        <div
                                          key={product.id}
                                          style={{
                                            border: "1px solid #f0f0f0",
                                            borderRadius: 8,
                                            background: "#fff",
                                            padding: 10,
                                          }}
                                        >
                                          <Checkbox value={product.id}>
                                            {product.reference
                                              ? `${product.name} (${product.reference})`
                                              : product.name}
                                          </Checkbox>
                                        </div>
                                      ))}
                                    </div>
                                  </Checkbox.Group>
                                </Form.Item>
                              )}
                            </div>
                          ),
                        };
                      })
                      .filter(Boolean)}
                  />
                )}
              </Form.List>
            )}
          </div>
        </Form>
      )}
    </Modal>
  );
};

ContractFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  initialData: PropTypes.shape({
    contractId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contractNumber: PropTypes.string,
    contractDescription: PropTypes.string,
    customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customerName: PropTypes.string,
    contractStart: PropTypes.string,
    contractEnd: PropTypes.string,
    contractPercentDiscount: PropTypes.number,
    contractIsActive: PropTypes.bool,
    contractLinesDto: PropTypes.arrayOf(
      PropTypes.shape({
        productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        totalLineDiscountPercent: PropTypes.number,
        contractLineNumber: PropTypes.string,
      }),
    ),
  }),
  readOnly: PropTypes.bool,
};

export default ContractFormModal;
